// #region Global Imports
import * as React from 'react';
import cx from 'classnames';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
// #endregion Global Imports
// #region Local Imports
/* NOTE: Imports the path from src */
import isEmpty from 'lodash/isEmpty';
import TrexLogo from '@components/Header/Logo/img/logo-m.svg';
import type { AuthorsFragment } from '@components/Authors/Authors';
import { Authors } from '@components/Authors/Authors';
import type { DateFragment } from '@components/Date';
import { Date } from '@components/Date';
import type { TagsFragment } from '@components/Taxonomies/Tags';
import { Tags } from '@components/Taxonomies/Tags';
import type { CategoriesFragment } from '@components/Taxonomies/Categories';
import { Categories } from '@components/Taxonomies/Categories';
import { gql } from '@generated/gql';
import type { FragmentType } from '@generated/fragment-masking';
import { getFragmentData } from '@generated/fragment-masking';
import type { IProps } from '@components/PostContent/Meta/PostMeta';
import type { DatelineFragment } from '@components/Taxonomies/Dateline';
import { Dateline } from '@components/Taxonomies/Dateline';
import { Icon } from '@components/Icon';
import styles from './Meta.module.sass';
// #endregion Local Imports

export const PostMetaFragment = gql(/* GraphQL */ `
  fragment PostMetaFragment on NodePost {
    issue: fieldIssue {
      id: targetId
      entity {
        id: uuid
        name
      }
    }

    date: fieldDate {
      date
      value
    }

    dictated: fieldDictated

    dateline: fieldDateline {
      id: targetId
      term: entity {
        uuid
        value: entityTranslation(language: $language) {
          id: entityUuid
          name: entityLabel
        }
      }
    }

    # we grab this only to manipulate it.
    paragraphs: fieldContent {
      entity {
        uuid: entityUuid
        translation: entityTranslation(language: $language) {
          ... on ParagraphMedia {
            media: fieldMedia {
              entity {
                ... on MediaAudio {
                  uuid
                }
              }
            }
          }
        }
      }
    }

    authors: fieldAuthors {
      ...AuthorFragment
    }

    categories: fieldCategory {
      id: targetId
      ...CategoryFragment
    }
  }
`);

export const PostMeta: React.FC<IProps.PostMeta> = ({
  termIssue,
  postMeta: _postMeta,
}) => {
  const router = useRouter();
  const entity = getFragmentData(PostMetaFragment, _postMeta);
  const issueFormatted = termIssue.concat(
    // \xa0 = No-Break Space
    '\xa0'.concat(String(entity.issue?.entity?.name)),
  );

  const languageId = router.locale === 'de' ? 0 : 1;
  const audioTerm = ['Anhören', 'Écouter'];
  const hasAudio = entity.dictated;

  return (
    <>
      <div className={styles.PostMeta__LogoPrint}>
        <NextImage
          alt="trex-logo"
          className={styles.PostMeta__LogoPrint_img}
          height={16}
          src={TrexLogo as string}
          width={16}
        />
        <span style={{ float: 'right' }}>
          https://www.trex.ch{String(router.asPath.split('?')[0])}
        </span>
      </div>

      {!isEmpty(entity.issue?.entity?.name) ? (
        <p className={cx(styles.PostMeta__issue, 'h1-small')}>
          {issueFormatted}
        </p>
      ) : null}

      {!isEmpty(entity.dateline) ? (
        <p className={styles.PostMeta__topic}>
          <Dateline entity={entity as FragmentType<typeof DatelineFragment>} />
        </p>
      ) : null}

      {!isEmpty(entity.date) ? (
        <p className={styles.PostMeta__byline}>
          <Date entity={entity as FragmentType<typeof DateFragment>} />

          {hasAudio ? (
            <span className={styles.PostMeta__audioText}>
              <span>&#160;&#160;&#183;&#160;</span>
              <Icon icon="headphones" inline />
              <span className={styles.PostMeta__audioText_inner}>
                &#160;{audioTerm[languageId]}
              </span>
            </span>
          ) : null}
        </p>
      ) : null}

      {!isEmpty(entity.categories) ? (
        <span
          style={{
            zIndex: '0',
            position: 'relative',
          }}
        >
          <Categories
            entity={entity as FragmentType<typeof CategoriesFragment>}
          />
        </span>
      ) : null}

      {!isEmpty(entity.authors) ? (
        <div className={styles.Post__AuthorsPrint_top}>
          <Authors entity={entity as FragmentType<typeof AuthorsFragment>} />
        </div>
      ) : null}
    </>
  );
};

export const PostAfterMetaFragment = gql(/* GraphQL */ `
  fragment PostAfterMetaFragment on NodePost {
    tags: fieldTags {
      id: targetId
      ...TagFragment
    }
  }
`);

export const PostAfterMeta: React.FC<IProps.PostAfterMeta> = ({
  postAfterMeta: _postAfterMeta,
}) => {
  const postAfterMeta = getFragmentData(PostAfterMetaFragment, _postAfterMeta);
  return !isEmpty(postAfterMeta.tags) ? (
    <span
      style={{
        zIndex: '0',
        position: 'relative',
      }}
    >
      <Tags entity={postAfterMeta as FragmentType<typeof TagsFragment>} />
    </span>
  ) : null;
};
